<script setup lang="ts">
import {
  ElForm,
  ElFormItem,
  ElInput,
  ElRow,
  ElCol,
  type FormRules,
  type FormInstance,
  type InputInstance,
} from "element-plus";
import { ref, reactive, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import CoreLink from "@/components/common/CoreLink/CoreLink.vue";
import useAuthStore from "@/stores/auth";
import { useCssVar } from "@vueuse/core";

const loginErrorCodes = [
  "UserNotConfirmedException",
  "PasswordResetRequiredException",
  "NotAuthorizedException",
  "UserNotFoundException",
  "LimitExceededException",
  "TooManyFailedAttemptsException",
  "TooManyRequestsException",
];
const mfaErrorCodes = [
  "ExpiredCodeException",
  "NotAuthorizedException",
  "CodeMismatchException",
];

const { t } = useI18n();
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const buttonColour = useCssVar("--so-color-secondary");
const state = reactive({
  loading: false,
  passwordError: "",
  mfaError: "",
  form: {
    email: "",
    password: "",
    mfa: "",
  },
});
const formRef = ref<FormInstance>();
const mfaRequired = ref(false);
const challengeUser = ref();
const mfaCodeInputRef = ref<InputInstance>();

const resetForm = () => {
  mfaRequired.value = false;
  state.form.mfa = "";
  state.form.email = "";
  state.form.password = "";
  authStore.logout;
};
const handleMfaError = (error: Error) => {
  state.form.mfa = "";
  if (mfaErrorCodes.includes(error.name)) {
    state.mfaError = t(`forms.login.mfa.challengeErrors.${error.name}`);
    formRef.value?.validateField("mfa", () => null);
    state.loading = false;
  }
};
const submitMfaCode = async () => {
  formRef.value?.clearValidate("mfa");
  state.loading = true;
  try {
    formRef.value?.validateField("mfa", () => null);
    await authStore.mfaLogin({
      user: challengeUser.value,
      mfa: state.form.mfa,
    });
  } catch (err) {
    if (!(err instanceof Error)) return;
    handleMfaError(err);
  } finally {
    navigateHome();
  }
};
const navigateHome = () => {
  state.passwordError = "";
  state.loading = false;
  if (route.name === "login") {
    router.push({ name: "home" });
  }
};
const submitForm = async () => {
  state.passwordError = "";
  state.mfaError = "";
  try {
    await formRef.value?.validate();
  } catch (error) {
    return;
  }

  state.loading = true;

  try {
    if (mfaRequired.value) {
      submitMfaCode();
    } else {
      challengeUser.value = await authStore.login({
        email: state.form.email,
        password: state.form.password,
      });
      if (challengeUser.value !== undefined && challengeUser.value) {
        mfaRequired.value = true;
      } else {
        mfaRequired.value = false;
      }
    }
  } catch (error) {
    if (error instanceof Error && loginErrorCodes.includes(error.name)) {
      state.passwordError = t(`forms.login.passwordFormErrors.${error.name}`);
      formRef.value?.validateField("password", () => null);
    }
  } finally {
    state.loading = false;
  }
};

const rules = reactive<FormRules>({
  email: [
    {
      required: true,
      message: () => t("forms.login.email.required"),
      trigger: "blur",
    },
    {
      type: "email",
      message: () => t("forms.login.email.invalid"),
      trigger: "blur",
    },
  ],
  password: [
    {
      required: true,
      message: () => t("forms.login.password.required"),
      trigger: "blur",
    },
    {
      validator: (rule, value, callback) => {
        if (state.passwordError) {
          callback(state.passwordError);
        }
        callback();
      },
    },
  ],
  mfa: [
    {
      required: true,
      max: 6,
      message: () => t("multiFactorDialog.errors.InvalidParameterException"),
      trigger: ["change", "blur"],
    },
    {
      validator: (rule, value, callback) => {
        if (state.mfaError) {
          callback(state.mfaError);
        }
        callback();
      },
    },
  ],
});

watch(mfaRequired, async (newMfaRequired) => {
  if (newMfaRequired) {
    await nextTick();
    mfaCodeInputRef.value?.focus();
  }
});
</script>

<template>
  <el-form
    ref="formRef"
    class="login-form"
    :model="state.form"
    scroll-to-error
    hide-required-asterisk
    :rules="rules"
    label-position="top"
    @keydown.enter.prevent="submitForm"
  >
    <el-form-item v-if="!mfaRequired">
      <h2
        v-cy="'loginHeader'"
        v-text="t('forms.login.pleaseLogIn')"
      />
    </el-form-item>
    <el-form-item
      v-if="!mfaRequired"
      ref="email"
      prop="email"
      :label="t('forms.login.email.label')"
    >
      <el-input
        v-if="!mfaRequired"
        v-model="state.form.email"
        v-cy="'usernameInput'"
        :disabled="state.loading"
        type="text"
        auto-complete="off"
      />
    </el-form-item>
    <el-form-item
      v-if="!mfaRequired"
      prop="password"
      :label="t('forms.login.password.label')"
    >
      <el-input
        v-if="!mfaRequired"
        v-model="state.form.password"
        v-cy="'passwordInput'"
        :disabled="state.loading"
        type="password"
        auto-complete="off"
      />
    </el-form-item>
    <el-form-item v-if="!mfaRequired">
      <el-row>
        <el-col
          :sm="12"
          :md="12"
        >
          <CoreButton
            v-cy="'submitLoginBtn'"
            :loading="state.loading"
            class="login-button"
            :color="buttonColour"
            @click="submitForm"
          >
            {{ t("forms.login.submit") }}
          </CoreButton>
        </el-col>
        <el-col
          :sm="12"
          :md="12"
          class="forgot"
        >
          <CoreLink
            :disabled="state.loading"
            :route="{ name: 'forgot' }"
            :underline="false"
          >
            {{ t("forms.login.forgotPassword.label") }}
          </CoreLink>
        </el-col>
      </el-row>
    </el-form-item>
    <el-form-item
      v-if="mfaRequired"
      ref="mfa"
      prop="mfa"
    >
      <h2>{{ t("forms.login.mfa.title") }}</h2>
      <br />
      <p>{{ t("forms.login.mfa.subtitle") }}</p>
      <el-input
        ref="mfaCodeInputRef"
        v-model="state.form.mfa"
        :disabled="state.loading"
        type="number"
        auto-complete="off"
        :placeholder="t('forms.login.mfa.placeholder')"
      />
    </el-form-item>
    <el-form-item>
      <el-row>
        <el-col
          :sm="12"
          :md="12"
        >
          <CoreButton
            v-if="mfaRequired"
            v-cy="'submitMfaCode'"
            :loading="state.loading"
            class="login-button"
            :color="buttonColour"
            @click="submitMfaCode"
          >
            {{ t("forms.login.mfa.verify") }}
          </CoreButton>
        </el-col>
        <el-col
          :sm="12"
          :md="12"
          class="forgot"
        >
          <CoreButton
            v-if="mfaRequired"
            plain
            type="info"
            class="login-button"
            @click="resetForm"
          >
            {{ t("forms.login.backAction") }}
          </CoreButton>
        </el-col>
      </el-row>
    </el-form-item>
  </el-form>
</template>

<style lang="scss" scoped>
.login-form {
  margin: 0 auto 30px;
  width: 100%;
}

.login-form > .so-form-item {
  margin-bottom: 30px;

  .so-row {
    width: 100%;
  }

  .login-button {
    height: 46px;
    font-family: var(--font-family-headings);
    font-weight: var(--font-bold);
    line-height: 1.5rem;
  }
  .forgot {
    text-align: center;
    margin-top: 30px;

    @media all and (min-width: $breakpoint-sm) {
      text-align: right;
      margin-top: 0;
    }
  }
}

.container {
  display: flex;
  align-content: flex-start;
  flex-direction: row;
}

.so-button {
  width: 100%;

  @media all and (min-width: $breakpoint-sm) {
    width: auto;
  }
}

/* The following CSS removes up and down arrows on the numbers input */
/* Chrome, Safari, Edge, Opera */
:deep(input::-webkit-outer-spin-button),
:deep(input::-webkit-inner-spin-button) {
  margin: 0;
  -webkit-appearance: none;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
